import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaArrowRight, FaCalendarAlt, FaPen, FaPlus, FaSearch, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import ErrorPage from "../../../components/ErrorPage";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import { useCan } from "../../../authz/useCan";
import { deleteSchoolYear } from "../api";
import { getCachedSchoolYears, hasSchoolYearListCache, loadSchoolYearList, removeCachedSchoolYear } from "../cache";
import { ActionButton, AvatarBlock, EmptyState, IconActionButton, LdsPage, ListSection, SearchPanel, StatusBadge, listRowClass } from "../components/Lds";
import type { AppError, EntityId, SchoolYear } from "../types";
import { formatDate } from "../../../utils/dateUtils";
import { entityKey, getTime, getYearNumber, groupSchoolYears, httpStatus } from "../utils";

type SortField = "startDate" | "endDate";
type SortOrder = "asc" | "desc";

const SchoolYears = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canListYears = useCan("school.years", "list");
  const canReadYears = useCan("school.years", "read");
  const canViewYearsGrant = useCan("school.years", "view");
  const canCrudYears = useCan("school.years", "crud");
  const canManageYears = useCan("school.years", "manage");
  const canViewYears = canListYears || canViewYearsGrant || canReadYears || canCrudYears || canManageYears;
  const canEditYears = canCrudYears || canManageYears;

  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>(() => getCachedSchoolYears() ?? []);
  const [searchQuery, setSearchQuery] = useState("");
  // No sort chosen: current year first, then the coming years, then the old ones (like the school year dropdown).
  const [sortBy, setSortBy] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [loading, setLoading] = useState(() => !hasSchoolYearListCache());
  const [error, setError] = useState<AppError | null>(null);
  const [yearToDelete, setYearToDelete] = useState<EntityId | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!canViewYears) {
        setError({ message: t("error.403.message", { defaultValue: "You do not have permission." }), status: 403 });
        setLoading(false);
        return;
      }
      try {
        const data = await loadSchoolYearList();
        if (!cancelled) setSchoolYears(data);
      } catch (err) {
        if (!cancelled && !hasSchoolYearListCache()) {
          setError({
            message:
              httpStatus(err) === 403
                ? t("error.403.message", { defaultValue: "You do not have permission." })
                : t("years.overview.load_failed", { defaultValue: "Failed to load school years." }),
            status: httpStatus(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [canViewYears, t]);

  const groupedYears = useMemo(() => {
    const groups = new Map<number, number>();
    schoolYears.forEach((year) => {
      const startYear = getYearNumber(year.startDate);
      if (startYear != null) groups.set(startYear, (groups.get(startYear) ?? 0) + 1);
    });
    return groups;
  }, [schoolYears]);

  const filteredYears = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return [...schoolYears]
      .filter((year) => {
        if (!query) return true;
        return `${getYearNumber(year.startDate) ?? ""} ${getYearNumber(year.endDate) ?? ""}`.includes(query);
      })
      .sort((a, b) => {
        if (!sortBy) return 0;
        const diff = getTime(a[sortBy]) - getTime(b[sortBy]);
        return sortOrder === "asc" ? diff : -diff;
      });
  }, [schoolYears, searchQuery, sortBy, sortOrder]);

  const yearGroups = useMemo(() => {
    if (sortBy) return [{ key: "sorted", label: null as string | null, years: filteredYears }];
    const groups = groupSchoolYears(filteredYears);
    return [
      { key: "current", label: t("years.overview.group_current", { defaultValue: "Current school year" }), years: groups.current },
      { key: "upcoming", label: t("years.overview.group_upcoming", { defaultValue: "Coming school years" }), years: groups.upcoming },
      { key: "past", label: t("years.overview.group_past", { defaultValue: "Old school years" }), years: groups.past },
    ].filter((group) => group.years.length > 0);
  }, [filteredYears, sortBy, t]);

  const handleSort = (field: SortField) => {
    setSortOrder((current) => (sortBy === field && current === "asc" ? "desc" : "asc"));
    setSortBy(field);
  };

  const sortIcon = (field: SortField) => {
    if (sortBy !== field) return <FaSort />;
    return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const handleDelete = async () => {
    if (!yearToDelete) return;
    try {
      await deleteSchoolYear(yearToDelete);
      removeCachedSchoolYear(yearToDelete);
      setSchoolYears((prev) => prev.filter((year) => String(year.id) !== String(yearToDelete)));
      setYearToDelete(null);
    } catch (err) {
      setError({
        message:
          httpStatus(err) === 403
            ? t("error.403.message", { defaultValue: "You do not have permission." })
            : t("years.overview.delete_failed", { defaultValue: "Failed to delete school year." }),
        status: httpStatus(err),
      });
    }
  };

  if (loading && schoolYears.length === 0) return <Spinner />;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;

  return (
    <LdsPage>
      <div className="space-y-5">
        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
                <FaCalendarAlt aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("school.overview.title", { defaultValue: "School structure" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("years.overview.title", { defaultValue: "School years" })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("years.overview.description", { defaultValue: "Manage school years and their default planning values." })}
                </p>
                {loading && schoolYears.length > 0 ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <StatusBadge tone="info">{t("common.refreshing", { defaultValue: "Refreshing" })}</StatusBadge>
                  </div>
                ) : null}
              </div>
            </div>

            {canEditYears ? (
              <div className="ml-auto shrink-0 lg:mr-36">
                <ActionButton onClick={() => navigate("/school/years/new")}>
                  <span className="inline-flex items-center gap-2"><FaPlus aria-hidden="true" /> {t("years.overview.add_button", { defaultValue: "Add school year" })}</span>
                </ActionButton>
              </div>
            ) : null}
          </div>

          <div className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex" aria-hidden="true">
            <FaCalendarAlt className="text-[110px] text-indigo-200/70 dark:text-indigo-800/40" />
          </div>
        </section>

        <SearchPanel>
          <div className="grid gap-4 lg:grid-cols-[minmax(240px,1fr)_auto] lg:items-center">
            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 dark:border-white/10 dark:bg-slate-950/50">
              <FaSearch className="text-slate-400" />
              <input
                type="text"
                placeholder={t("years.common.search", { defaultValue: "Search school years" })}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm text-slate-950 outline-none placeholder:text-slate-400 dark:text-white"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <ActionButton tone="secondary" onClick={() => handleSort("startDate")}>
                <span className="inline-flex items-center gap-2">{t("years.common.start_date", { defaultValue: "Start date" })} {sortIcon("startDate")}</span>
              </ActionButton>
              <ActionButton tone="secondary" onClick={() => handleSort("endDate")}>
                <span className="inline-flex items-center gap-2">{t("years.common.end_date", { defaultValue: "End date" })} {sortIcon("endDate")}</span>
              </ActionButton>
            </div>
          </div>
        </SearchPanel>

        <ListSection
          title={t("years.overview.list_title", { defaultValue: "School year overview" })}
          count={t("common.result_count", { count: filteredYears.length, defaultValue: "{{count}} results" })}
        >
          {filteredYears.length > 0 && (
            <div className="hidden px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 lg:grid lg:grid-cols-[minmax(260px,1fr)_minmax(180px,0.6fr)_minmax(180px,0.6fr)_180px_220px] lg:gap-4">
              <span>{t("years.overview.column_year", { defaultValue: "School year" })}</span>
              <button type="button" onClick={() => handleSort("startDate")} className="inline-flex items-center gap-1.5 text-left uppercase hover:text-slate-700 dark:hover:text-slate-200">
                {t("years.common.start_date", { defaultValue: "Start date" })} {sortIcon("startDate")}
              </button>
              <button type="button" onClick={() => handleSort("endDate")} className="inline-flex items-center gap-1.5 text-left uppercase hover:text-slate-700 dark:hover:text-slate-200">
                {t("years.common.end_date", { defaultValue: "End date" })} {sortIcon("endDate")}
              </button>
              <span>{t("years.overview.column_status", { defaultValue: "Status" })}</span>
              <span />
            </div>
          )}
          {yearGroups.map((group) => (
            <React.Fragment key={group.key}>
              {group.label && (
                <div data-year-group={group.key}
                  className="bg-slate-50/70 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-white/[0.03] dark:text-slate-400">
                  {group.label}
                </div>
              )}
              {group.years.map((year) => {
                const startYear = getYearNumber(year.startDate);
                const endYear = getYearNumber(year.endDate);
                const duplicateCount = startYear != null ? groupedYears.get(startYear) ?? 0 : 0;
                const label = `${startYear ?? "—"} - ${endYear ?? "—"}`;
                return (
                  <article
                    key={entityKey(year.id)}
                    onClick={() => navigate(`/school/years/${year.id}/details`)}
                    className={`${listRowClass} lg:grid-cols-[minmax(260px,1fr)_minmax(180px,0.6fr)_minmax(180px,0.6fr)_180px_220px]`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <AvatarBlock tone="emerald">{startYear ? String(startYear).slice(2) : "YR"}</AvatarBlock>
                      <div className="min-w-0">
                        <h2 className="truncate text-base font-semibold text-slate-950 dark:text-white">{label}</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{t("years.overview.row_subtitle", { defaultValue: "Planning period" })}</p>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{formatDate(year.startDate)}</p>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{formatDate(year.endDate)}</p>
                    <div>{duplicateCount > 1 ? <StatusBadge tone="warn">{t("years.overview.duplicate_start", { defaultValue: "Duplicate start year" })}</StatusBadge> : <StatusBadge tone="success">{t("common.active", { defaultValue: "Active" })}</StatusBadge>}</div>
                    <div className="flex flex-wrap items-center justify-start gap-2 lg:justify-end">
                      <ActionButton tone="secondary" onClick={() => navigate(`/school/years/${year.id}/details`)}>
                        <span className="inline-flex items-center gap-2">{t("common.actions.view", { defaultValue: "View" })}<FaArrowRight aria-hidden="true" /></span>
                      </ActionButton>
                      {canEditYears ? (
                        <>
                          <IconActionButton label={t("common.actions.edit", { defaultValue: "Edit" })} onClick={() => navigate(`/school/years/${year.id}/edit`)}>
                            <FaPen aria-hidden="true" />
                          </IconActionButton>
                          <IconActionButton label={t("common.actions.delete", { defaultValue: "Delete" })} tone="danger" onClick={() => setYearToDelete(year.id)}>
                            <FaTrash aria-hidden="true" />
                          </IconActionButton>
                        </>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </React.Fragment>
          ))}
          {filteredYears.length === 0 ? <div className="p-5"><EmptyState>{t("years.overview.none_available", { defaultValue: "No school years available." })}</EmptyState></div> : null}
        </ListSection>
      </div>

      <Modal
        show={yearToDelete != null}
        title={t("common.modal.are_you_sure", { defaultValue: "Are you sure?" })}
        message={t("common.modal.action_cannot_be_undone", { defaultValue: "This action cannot be undone." })}
        type="confirm"
        isConfirm
        onConfirm={handleDelete}
        onClose={() => setYearToDelete(null)}
      />
    </LdsPage>
  );
};

export default SchoolYears;
